import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const SYSTEM_CONFIG = {
  hpo: {
    label: 'Phenotypes',
    borderColor: '#1565c0',
    selectedBg: '#1565c0',
    filterBg: '#e3f2fd',
    filterActiveBg: '#1565c0'
  },
  'icd-10-cm': {
    label: 'Diseases',
    borderColor: '#c42f68',
    selectedBg: '#c42f68',
    filterBg: '#fce4ec',
    filterActiveBg: '#c42f68'
  },
  hgnc: {
    label: 'Genotypes',
    borderColor: '#2e7d32',
    selectedBg: '#2e7d32',
    filterBg: '#e8f5e9',
    filterActiveBg: '#2e7d32'
  }
};

const ALL_SYSTEMS = Object.keys(SYSTEM_CONFIG);

export default function ClinicalSuggestionsPanel({
  suggestions = [],
  analyzingNote = false,
  selectedSuggestions = [],
  toggleSuggestion,
  onAccept,
  savingNote = false,
  disabled = false
}) {
  const [activeSystems, setActiveSystems] = useState(new Set(ALL_SYSTEMS));

  if (disabled || (!suggestions.length && !analyzingNote)) return null;

  const toggleSystem = (sys) => {
    setActiveSystems((prev) => {
      const next = new Set(prev);
      if (next.has(sys)) next.delete(sys);
      else next.add(sys);
      return next;
    });
  };

  const countBySys = {};
  for (const item of suggestions) {
    if (item.type) countBySys[item.type] = (countBySys[item.type] || 0) + 1;
  }

  const availableSystems = ALL_SYSTEMS.filter((s) => countBySys[s] > 0);
  const visibleSuggestions = suggestions.filter((i) =>
    activeSystems.has(i.type)
  );

  return (
    <Grid size={12}>
      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          {analyzingNote ? (
            <>
              <CircularProgress size={16} />
              Analyzing note...
            </>
          ) : (
            'Suggested Clinical Terms'
          )}
        </Typography>

        {availableSystems.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            {availableSystems.map((sys) => {
              const cfg = SYSTEM_CONFIG[sys];
              const active = activeSystems.has(sys);
              return (
                <Chip
                  key={sys}
                  label={`${cfg.label} (${countBySys[sys]})`}
                  size="small"
                  onClick={() => toggleSystem(sys)}
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: active ? cfg.filterActiveBg : cfg.filterBg,
                    color: active ? '#fff' : cfg.filterActiveBg,
                    border: `1px solid ${cfg.filterActiveBg}`,
                    '&:hover': {
                      backgroundColor: active
                        ? cfg.filterActiveBg
                        : `${cfg.filterActiveBg}22`
                    }
                  }}
                />
              );
            })}
          </Box>
        )}

        {visibleSuggestions.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
            {visibleSuggestions.map((item) => {
              const cfg =
                SYSTEM_CONFIG[item.type] || SYSTEM_CONFIG['icd-10-cm'];
              const isSelected = selectedSuggestions.some(
                (s) => s.id === item.id
              );
              return (
                <Tooltip key={item.id} title={item.id} placement="top" arrow>
                  <Chip
                    label={
                      <span>
                        {item.name}{' '}
                        <span style={{ opacity: 0.7, fontSize: '0.75em' }}>
                          [{item.id}]
                        </span>
                      </span>
                    }
                    onClick={() => toggleSuggestion(item)}
                    size="small"
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? cfg.selectedBg
                        : 'transparent',
                      color: isSelected ? '#fff' : 'inherit',
                      border: `1px solid ${cfg.borderColor}`,
                      '&:hover': {
                        backgroundColor: isSelected
                          ? cfg.selectedBg
                          : `${cfg.borderColor}18`
                      }
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        )}

        {!analyzingNote &&
          visibleSuggestions.length === 0 &&
          suggestions.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary'
              }}
            >
              No suggestions visible — enable a system filter above.
            </Typography>
          )}

        {!analyzingNote && selectedSuggestions.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={onAccept}
              disabled={savingNote}
              startIcon={
                savingNote ? <CircularProgress size={16} /> : <AddIcon />
              }
            >
              Add to Patient Record
            </Button>
          </Box>
        )}
      </Box>
    </Grid>
  );
}
